import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Smartphone, Monitor, Loader2, LogOut } from "lucide-react";

export default function Security() {
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, ss] = await Promise.all([api.get("/auth/mfa/status"), api.get("/auth/sessions")]);
    setStatus(s.data); setSessions(ss.data);
  };
  useEffect(() => { load(); }, []);

  const begin = async () => { setBusy(true); try { setSetup((await api.post("/auth/mfa/setup")).data); } finally { setBusy(false); } };
  const enable = async () => { setBusy(true); try { await api.post("/auth/mfa/enable", { code }); toast.success("Two-factor enabled."); setSetup(null); setCode(""); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const disable = async () => { setBusy(true); try { await api.post("/auth/mfa/disable", { code: disableCode }); toast.success("Two-factor disabled."); setDisableCode(""); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const revoke = async (id) => { try { await api.delete(`/auth/sessions/${id}`); toast.success("Session revoked."); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const revokeAll = async () => { try { await api.delete("/auth/sessions"); toast.success("Signed out of other devices."); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  if (!status) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-400" /><h1 className="font-display text-2xl font-bold">Security</h1></div>

      <div className="card-emerald p-6" data-testid="mfa-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display font-semibold flex items-center gap-2"><Smartphone className="w-4 h-4 text-emerald-400" /> Two-factor authentication</h2>
            <p className="text-sm text-muted-foreground mt-1">Use Google Authenticator or Authy for a second sign-in step.{status.required && " Required for your account."}</p>
          </div>
          <Badge className={status.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"} data-testid="mfa-state">{status.enabled ? "Enabled" : "Disabled"}</Badge>
        </div>

        {!status.enabled && !setup && <Button onClick={begin} disabled={busy} className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="mfa-begin-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set up two-factor"}</Button>}

        {setup && (
          <div className="mt-4 flex flex-col items-center gap-3 border-t border-white/10 pt-4">
            <p className="text-sm text-muted-foreground">Scan with your authenticator app, then enter the 6-digit code.</p>
            <img src={setup.qr_data_url} alt="MFA QR" className="w-44 h-44 rounded-lg bg-white p-2" data-testid="mfa-qr" />
            <p className="text-xs text-muted-foreground">Manual key: <span className="font-mono text-emerald-300">{setup.secret}</span></p>
            <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="000000" className="w-40 text-center font-mono text-xl tracking-widest bg-[#0A0D0C] border-emerald-500/20" data-testid="mfa-code-input" />
            <Button onClick={enable} disabled={busy || code.length < 6} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="mfa-enable-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & enable"}</Button>
          </div>
        )}

        {status.enabled && !status.required && (
          <div className="mt-4 border-t border-white/10 pt-4 flex items-end gap-2">
            <div><p className="text-xs text-muted-foreground mb-1">Enter a current code to disable</p><Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} inputMode="numeric" maxLength={6} placeholder="000000" className="w-40 text-center font-mono bg-[#0A0D0C] border-emerald-500/20" data-testid="mfa-disable-input" /></div>
            <Button onClick={disable} disabled={busy || disableCode.length < 6} variant="outline" className="border-red-500/40 text-red-300" data-testid="mfa-disable-btn">Disable</Button>
          </div>
        )}
      </div>

      <div className="card-emerald p-6" data-testid="sessions-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-semibold flex items-center gap-2"><Monitor className="w-4 h-4 text-emerald-400" /> Active sessions</h2>
          <Button onClick={revokeAll} size="sm" variant="outline" data-testid="revoke-all-btn"><LogOut className="w-4 h-4 mr-1" /> Sign out others</Button>
        </div>
        <div className="space-y-2">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-lg bg-white/5 p-3" data-testid="session-row">
              <div className="min-w-0">
                <p className="text-sm truncate">{s.user_agent || "Unknown device"} {s.current && <span className="ml-1 inline-block rounded-full bg-emerald-500/20 text-emerald-300 text-xs px-2 py-0.5">This device</span>}</p>
                <p className="text-xs text-muted-foreground">{s.ip || "—"} · last seen {new Date(s.last_seen_at).toLocaleString()}</p>
              </div>
              {!s.current && <Button size="sm" variant="ghost" onClick={() => revoke(s.id)} className="text-red-400" data-testid="revoke-session-btn">Revoke</Button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
